import { BinaryHelper } from '../../../serialization/binary-helper.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import {
  cloneAsSet, cloneMap, isSameData, isSameList, isSameMap, mapToData,
} from '../../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { TicketTemplate } from '../ticket-template';
import { ITicketPoolSeatingModule } from './ticket-pool-seating-module.js';
import { TicketPoolStrategy } from './ticket-pool-strategy.js';
import { ITicketPool } from './ticket-pool.js';

export class StandardTicketPool extends ITicketPool implements ITicketPoolSeatingModule {
  static readonly TYPE_ID: string = '95651766429f479792a02ab53bf316b3';

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly reservedSeats: Set<number>,
    public readonly reservedTickets: Map<string, number>,
  ) {
    super();
  }

  public readonly strategy: TicketPoolStrategy = TicketPoolStrategy.Standard;

  get $typeId(): string {
    return StandardTicketPool.TYPE_ID;
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string,
    reservedSeats: Set<number>,
    reservedTickets: Map<string, number>,
  }): StandardTicketPool {
    return new StandardTicketPool(
        args.businessRef,
        args.eventId,
        args.reservedSeats,
        args.reservedTickets,
    );
  }

  static empty(args: {
    businessRef: BusinessModuleRef,
    eventId: string,
  }): StandardTicketPool {
    return new StandardTicketPool(
        args.businessRef,
        args.eventId,
        new Set<number>(),
        new Map<string, number>(),
    );
  }

  public getSeatingModule(): ITicketPoolSeatingModule | null {
    return this;
  }

  public getTicketCount(templateId: string): number {
    return this.reservedTickets.get(templateId) ?? 0;
  }

  public isSoldOut(template: TicketTemplate): boolean {
    if(template.quantity.isInfinite) {
      return false;
    }

    return this.getTicketCount(template.uniqueId) >= template.quantity.value;
  }

  isAnySeatReserved(seats: number[]): boolean {
    return seats.some((seat) => this.reservedSeats.has(seat));
  }

  isSeatReserved(seat: number): boolean {
    return this.reservedSeats.has(seat);
  }

  static fromMap(context: SerializationContext, map: SerializedData): StandardTicketPool {
    const rawData: unknown = MapUtils.getRaw(map, 'reservedSeats');
    const seats: number[] = isNotNullOrUndefined(rawData) ?
        BinaryHelper.fromBytesToIntegers(context.fromBinaryBlob(rawData)) : [];

    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      reservedSeats: new Set<number>(seats),
      reservedTickets: MapUtils.getMap(map, 'reservedTickets', (k, v) => ({
        key: k,
        value: JSONUtils.decodeNumber(v),
      })),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'reservedSeats': context.toBinaryBlob(BinaryHelper.fromIntegersToBytes([...this.reservedSeats])),
      'reservedTickets': mapToData(this.reservedTickets, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return StandardTicketPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      reservedSeats: cloneAsSet(this.reservedSeats),
      reservedTickets: cloneMap(this.reservedTickets, (v) => v),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      isSameList([...other.reservedSeats], [...this.reservedSeats]) && // this is a horrible way to do it
      isSameMap(other.reservedTickets, this.reservedTickets, (lh, rh) => lh === rh);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof StandardTicketPool;
  }

  static assertType(order: DataModel): order is StandardTicketPool {
    return order instanceof StandardTicketPool;
  }
}

export class StandardTicketPoolBuilder extends DataModelBuilder<StandardTicketPool> {
  build(context: SerializationContext, map: SerializedData): StandardTicketPool {
    return StandardTicketPool.fromMap(context, map);
  }
}
